<script lang="ts" setup>
import NumberField from '../form-number/NumberField.vue';
import { formInputClasses, formInputSizeClasses } from '../form-control/classes';

defineOptions({
    inheritAttrs: false
});

const model = defineModel<any>();
const props = withDefaults(defineProps<{
    size?: 'sm' | 'md' | 'lg';
    fluid?: boolean;
    disabled?: boolean;
    readonly?: boolean;
}>(), {
    size: 'md',
    fluid: true,
    disabled: false,
    readonly: false
});
const attrs = useAttrs();
const input = ref<HTMLInputElement | null>(null);
const isNumber = computed(() => String(attrs.type || '').toLowerCase() === 'number');
const numericValue = computed(() =>
{
    if (model.value === '' || model.value === null || model.value === undefined)
        return Number.NaN;

    return typeof model.value === 'number' || typeof model.value === 'string' ? Number(model.value) : Number.NaN;
});

function bound(value: unknown)
{
    const parsed = value === undefined || value === null || value === '' ? Number.NaN : Number(value);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
}

const decrementDisabled = computed(() => Number.isFinite(numericValue.value) && numericValue.value <= bound(attrs.min));
const incrementDisabled = computed(() => Number.isFinite(numericValue.value) && numericValue.value >= bound(attrs.max));

function inputAttrs()
{
    return Object.fromEntries(Object.entries(attrs).filter(([name]) => name !== 'class' && name !== 'style'));
}

function focus(options?: FocusOptions)
{
    input.value?.focus(options);
}

function blur()
{
    input.value?.blur();
}

function select()
{
    input.value?.select();
}

function step(delta: -1 | 1)
{
    const control = input.value;
    if (!control || props.disabled || props.readonly || control.matches(':disabled') || control.readOnly)
        return;

    const previous = control.value;
    try
    {
        if (control.step === 'any')
        {
            const current = Number.isFinite(control.valueAsNumber) ? control.valueAsNumber : 0;
            const minimum = bound(control.min);
            const maximum = bound(control.max);
            if (Number.isFinite(minimum) && Number.isFinite(maximum) && minimum > maximum)
                return;

            let next = current + delta;
            if (Number.isFinite(minimum)) next = Math.max(minimum, next);
            if (Number.isFinite(maximum)) next = Math.min(maximum, next);
            control.value = String(next);
        }
        else if (delta > 0)
            control.stepUp();
        else
            control.stepDown();
    }
    catch
    {
        return;
    }

    if (control.value === previous)
        return;

    const EventType = control.ownerDocument.defaultView?.Event ?? Event;
    control.dispatchEvent(new EventType('input', { bubbles: true }));
    control.dispatchEvent(new EventType('change', { bubbles: true }));
}

defineExpose({
    focus,
    blur,
    select,
    get $el()
    {
        return input.value;
    }
});

const classes = computed(() => [
    formInputClasses,
    formInputSizeClasses[props.size],
    {
        'w-full': props.fluid
    }
]);

function tabIndex()
{
    if (props.disabled)
        return -1;

    return attrs.tabindex ?? attrs.tabIndex;
}

function blurInactiveControl(event: FocusEvent)
{
    if (props.disabled)
        (event.target as HTMLInputElement).blur();
}

function preventInactiveFocus(event: PointerEvent)
{
    if (props.disabled)
        event.preventDefault();
}
</script>

<template>
    <NumberField
        v-if="isNumber"
        :class="attrs.class"
        :style="attrs.style"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :decrement-disabled="decrementDisabled"
        :increment-disabled="incrementDisabled"
        :size="props.size"
        :fluid="props.fluid"
        :input-id="typeof attrs.id === 'string' ? attrs.id : undefined"
        :aria-label="String(attrs['aria-label'] ?? attrs.name ?? '') || undefined"
        @step="step"
    >
        <input
            ref="input"
            v-model="model"
            v-bind="inputAttrs()"
            :class="classes"
            :disabled="props.disabled"
            :readonly="props.readonly"
            :tabindex="tabIndex()"
            @focus="blurInactiveControl"
            @pointerdown="preventInactiveFocus"
        >
    </NumberField>
    <input
        v-else
        ref="input"
        v-model="model"
        v-bind="$attrs"
        :class="classes"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :tabindex="tabIndex()"
        @focus="blurInactiveControl"
        @pointerdown="preventInactiveFocus"
    >
</template>
